import { Box, Slab } from '../../render/parts'
import { Keyboard, Monitor } from '../parts/tech'
import { DESK_HEIGHT, Grommet, PanelLegs, Pedestal, WorkTop } from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Bench island for four people: two 2.8 m runs back to back, split by a slot
 * that the cross-shaped accent screens drop through. Each 1.4 x 0.7 position
 * gets a mobile pedestal parked on its outer side.
 */

const WIDTH = 2.8
const DEPTH = 1.4
/** Slot between the two runs; the spine screen is thinner than this, so nothing touches. */
const SPINE_GAP = 0.03
const RUN_DEPTH = (DEPTH - SPINE_GAP) / 2
const RUN_Z = (RUN_DEPTH + SPINE_GAP) / 2

const SCREEN_TOP = 1.14
const SCREEN_THICKNESS = 0.02
/** The spine reaches down to the underframe beam; the cross blade only sits on the tops. */
const SPINE_BOTTOM = 0.65
const CROSS_BOTTOM = DESK_HEIGHT - 0.005

/** One accent blade with an aluminium cap rail. `turned` runs it along Z instead of X. */
function ScreenBlade({
  length,
  bottom,
  x = 0,
  turned = false,
  accent,
}: {
  length: number
  bottom: number
  x?: number
  turned?: boolean
  accent?: string
}) {
  const height = SCREEN_TOP - bottom
  return (
    <group position={[x, 0, 0]} rotation={[0, turned ? Math.PI / 2 : 0, 0]}>
      <Slab
        tone="accent"
        color={accent}
        size={[length, height, SCREEN_THICKNESS]}
        radius={0.01}
        rotation={[Math.PI / 2, 0, 0]}
        position={[0, bottom + height / 2, 0]}
      />
      <Slab
        tone="metal"
        size={[length - 0.012, SCREEN_THICKNESS + 0.008, 0.016]}
        radius={0.007}
        position={[0, SCREEN_TOP - 0.002, 0]}
      />
    </group>
  )
}

/** The four seats: worktop centre, which run they sit on, and where their pedestal parks. */
const SEATS = [
  { id: 'front-left', x: -0.7, sz: 1, pedestalX: -1.05 },
  { id: 'front-right', x: 0.7, sz: 1, pedestalX: 1.05 },
  { id: 'back-left', x: -0.7, sz: -1, pedestalX: -1.05 },
  { id: 'back-right', x: 0.7, sz: -1, pedestalX: 1.05 },
] as const

function DeskCluster4({ accent, variant = 'screen' }: AssetContext) {
  const withScreens = variant !== 'plain'
  const dressed = variant === 'dressed'

  return (
    <group>
      {[1, -1].map((side) => (
        <group key={side} position={[0, 0, side * RUN_Z]}>
          <WorkTop width={WIDTH} depth={RUN_DEPTH} />
        </group>
      ))}

      <PanelLegs width={WIDTH} depth={DEPTH} />
      {/* Longitudinal beam under the slot, tying both end panels together. */}
      <Box tone="shellSoft" size={[WIDTH - 0.1, 0.07, 0.1]} position={[0, 0.62, 0]} />

      <Grommet x={-0.7} z={0.58} r={0.03} />
      <Grommet x={0.7} z={-0.58} r={0.03} />

      {SEATS.map((seat) => (
        <group
          key={seat.id}
          position={[seat.pedestalX, 0, seat.sz * 0.36]}
          rotation={[0, seat.sz > 0 ? 0 : Math.PI, 0]}
        >
          <Pedestal width={0.4} depth={0.5} height={0.56} drawers={2} mobile accent={accent} />
        </group>
      ))}

      {withScreens ? (
        <group>
          {/* Spine runs the full length in two overlapping halves so the cross reads clean. */}
          <ScreenBlade length={WIDTH / 2 + 0.004} bottom={SPINE_BOTTOM} x={-WIDTH / 4 + 0.002} accent={accent} />
          <ScreenBlade length={WIDTH / 2 + 0.004} bottom={SPINE_BOTTOM} x={WIDTH / 4 - 0.002} accent={accent} />
          <ScreenBlade length={DEPTH} bottom={CROSS_BOTTOM} turned accent={accent} />
        </group>
      ) : null}

      {dressed
        ? SEATS.map((seat) => (
            <group key={seat.id} position={[seat.x, 0, 0]} rotation={[0, seat.sz > 0 ? 0 : Math.PI, 0]}>
              <group position={[0, 0, 0.2]}>
                <Monitor width={0.5} height={0.3} base={DESK_HEIGHT} />
              </group>
              <group position={[0, 0, 0.52]}>
                <Keyboard base={DESK_HEIGHT} />
              </group>
            </group>
          ))
        : null}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'desk-cluster-4',
  name: 'Isla de 4 puestos',
  category: 'desks',
  footprint: [WIDTH, 1.5],
  height: 1.16,
  surfaceHeight: DESK_HEIGHT,
  seats: 4,
  occupantSide: 'both',
  accentable: true,
  variants: [
    { id: 'screen', name: 'Con paneles' },
    { id: 'plain', name: 'Sin paneles' },
    { id: 'dressed', name: 'Equipada' },
  ],
  keywords: [
    'isla',
    'bench',
    'cluster',
    'cuatro puestos',
    'escritorio compartido',
    'desk island',
    'workstation',
  ],
  Component: DeskCluster4,
}
